import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  RiskSuggestion,
  normaliseRiskSuggestions,
  riskCategoryLabel,
  riskSeverityLabel,
} from './risk-suggestion.model';

/**
 * The AI's suggested facility issues, read-only, with an add button per row.
 *
 * ONE component, TWO callers — E2 (`patient-ai-request-detail`, from
 * `AIRequest.suggestedRiskItems`) and F2 (`patient-note-detail`, from the ward-round hand-off).
 * F2 explicitly requires reuse rather than a second copy of the markup, and the reason is D8: the
 * verbatim quote must be on screen before a doctor can put the sentence on their own letterhead,
 * and a control that appears twice is a control that can be relaxed in one place and not the
 * other (R4).
 *
 * Renders NOTHING when there are no suggestions. `suggestedRiskItems` is NULL — not `[]` — for
 * every request predating this feature and every recording where extraction found nothing, so the
 * empty case is the common one and is silent, not an empty-state message.
 */
@Component({
  selector: 'app-risk-suggestion-list',
  templateUrl: './risk-suggestion-list.component.html',
  styleUrls: ['./risk-suggestion-list.component.scss'],
})
export class RiskSuggestionListComponent implements OnChanges {
  /**
   * Accepted RAW, in any of the shapes §5a's column actually arrives in — `null`,
   * `{ items: [...] }` or a bare array. Normalised once per change rather than in a getter, so
   * `*ngFor` is not handed a new array identity on every change-detection pass.
   */
  @Input() suggestions: any = null;

  /**
   * The issue text of every item ALREADY confirmed onto the register, for the dedupe guard (E4).
   * Passed in rather than computed here: the owning component is the one that knows which list of
   * confirmed items is in scope (this patient's, in both callers).
   */
  @Input() addedIssues: string[] | null = null;

  @Input() heading: string = 'Suggested Facility Issues';

  /** Disables every add button — used while a create is in flight, or on a read-only surface. */
  @Input() disabled: boolean = false;
  /**
   * Show the "Add New Item" button in the heading row, mirroring TASK LIST.
   *
   * Opt-in rather than always-on: this component renders on surfaces that have no manual-add
   * path, and a button that opens nothing is worse than no button.
   */
  @Input() showAddNew = false;


  @Output() addSuggestion = new EventEmitter<{ event: MouseEvent; suggestion: RiskSuggestion }>();

  /** Manual add — carries no suggestion, so the dialog opens empty (D13). */
  @Output() addNew = new EventEmitter<void>();

  items: RiskSuggestion[] = [];

  private addedLookup = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions']) {
      this.items = normaliseRiskSuggestions(this.suggestions);
    }
    if (changes['addedIssues']) {
      this.addedLookup = new Set(
        (this.addedIssues || [])
          .filter(issue => !!issue)
          .map(issue => String(issue).trim().toLowerCase())
      );
    }
  }

  /**
   * E4's dedupe guard, mirroring `canAddTask` — case-insensitive on the issue text, which is the
   * only thing a suggestion and a confirmed item reliably share (the item's own `id` has no
   * counterpart on the suggestion, and `quote` may be absent on a hand-added item).
   *
   * KNOWN LIMITATION, matching TASK LIST rather than fixed here (user call, 2026-09-01):
   * the issue text is EDITABLE in the add dialog. Edit it on the way in — "no stock of
   * stronger pain relief" saved as "no stock of STILPANE" — and this no longer matches, so
   * the add button stays and offers a second copy of something already on the register.
   *
   * `canAddTask` has the identical flaw for the identical reason, so risk behaves like tasks
   * and both should be fixed together. The fix is to key on `quote` first (D8 makes it
   * verbatim and non-editable, so it cannot drift) and fall back to text for hand-added
   * items, which have no quote. See tasks/risk-management.md §27.
   */
  canAdd(suggestion: RiskSuggestion): boolean {
    const issue = suggestion?.issue;
    if (!issue) return false;

    return !this.addedLookup.has(String(issue).trim().toLowerCase());
  }
  onAddNew(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) return;
    this.addNew.emit();
  }


  onAdd(event: MouseEvent, suggestion: RiskSuggestion): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) return;
    this.addSuggestion.emit({ event, suggestion });
  }

  categoryLabel(category?: string): string {
    return riskCategoryLabel(category);
  }

  severityLabel(severity?: string): string {
    return riskSeverityLabel(severity);
  }

  /** Drives the severity chip's colour. Unknown severities get the neutral class, not 'high'. */
  severityClass(severity?: string): string {
    const value = String(severity || '').toLowerCase();
    return value === 'high' || value === 'medium' || value === 'low'
      ? `risk-severity-${value}`
      : 'risk-severity-unknown';
  }

  trackBySuggestion(index: number, suggestion: RiskSuggestion): string {
    return `${index}:${suggestion?.issue || ''}`;
  }
}
